import React, { useEffect, useRef, useState, useCallback } from 'react';
import Styles from './LanguageSelector.module.css';
import USAFlag from '../../../images/usa-flag.png';
import SpainFlag from '../../../images/spain-flag.png';
import FrenchFlag from '../../../images/french_flag.png';

// Define a type for the language options
interface LanguageOption {
    code: string;
    flag: string;
    name: string;
}

declare global {
    interface Window {
        google: any;
        googleTranslateElementInit?: () => void;
    }
}

const TranslateComponent: React.FC = () => {
    const googleTranslateRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>({ code: 'en', flag: USAFlag, name: 'English' });
    const [isOpen, setIsOpen] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isChangingLanguage, setIsChangingLanguage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const options: LanguageOption[] = [
        { code: 'en', flag: USAFlag, name: 'English' },
        { code: 'es', flag: SpainFlag, name: 'Spanish' },
        { code: 'fr', flag: FrenchFlag, name: 'French' },
    ];

    const toggleDropdown = () => {
        setIsOpen((prev) => !prev);
    };

    const getCurrentLanguage = (): string => {
        try {
            // Check localStorage for saved language preference first
            const savedLanguage = localStorage.getItem('selectedLanguage');
            if (savedLanguage && options.some((opt) => opt.code === savedLanguage)) {
                return savedLanguage;
            }

            // Check if Google Translate is already initialized and has a selected language
            const languageSelector = document.querySelector('.goog-te-combo') as HTMLSelectElement;
            if (languageSelector && languageSelector.value) {
                return languageSelector.value;
            }

            return 'en'; // Default to English
        } catch (error) {
            console.warn('Error getting current language:', error);
            return 'en';
        }
    };

    const getLanguageOption = (code: string): LanguageOption => {
        return options.find((option) => option.code === code) || options[0];
    };

    const changeLanguageSafely = useCallback((languageCode: string) => {
        try {
            // Only change if Google Translate is properly initialized
            if (!window.google || !window.google.translate) {
                console.warn('Google Translate not initialized');
                return;
            }

            const languageSelector = document.querySelector('.goog-te-combo') as HTMLSelectElement;
            if (languageSelector) {
                // Set the value without triggering events immediately
                languageSelector.value = languageCode;

                // Use a single, simple change event
                setTimeout(() => {
                    try {
                        languageSelector.dispatchEvent(new Event('change'));
                    } catch (e) {
                        console.warn('Error dispatching change event:', e);
                    }
                }, 100);
            }
        } catch (error) {
            console.warn('Error changing language safely:', error);
        }
    }, []);

    const handleOptionSelect = (option: LanguageOption) => {
        if (isChangingLanguage || isLoading) return; // Prevent multiple rapid changes

        setSelectedLanguage(option);
        setIsOpen(false);

        // Save language preference to localStorage for axios interceptor
        localStorage.setItem('selectedLanguage', option.code);

        // Also dispatch a custom event to notify other components of language change
        window.dispatchEvent(
            new CustomEvent('languageChanged', {
                detail: { language: option.code },
            }),
        );

        if (!window.google || !window.google.translate) {
            loadGoogleTranslateScript();
        } else {
            setIsChangingLanguage(true);
            setIsLoading(true);

            // Simple language change
            changeLanguageSafely(option.code);

            // Reset loading state after a delay
            setTimeout(() => {
                setIsChangingLanguage(false);
                setIsLoading(false);
            }, 1000);
        }
    };

    const loadGoogleTranslateScript = () => {
        try {
            // Remove any existing script to prevent conflicts
            const existingScript = document.querySelector('script[src*="translate.google.com"]');
            if (existingScript) {
                existingScript.remove();
            }

            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
            script.onerror = () => {
                console.warn('Failed to load Google Translate script');
                setIsLoading(false);
            };
            document.body.appendChild(script);
        } catch (error) {
            console.warn('Error loading Google Translate script:', error);
            setIsLoading(false);
        }
    };

    const initializeGoogleTranslate = (languageCode: string) => {
        try {
            if (
                googleTranslateRef.current &&
                window.google &&
                window.google.translate &&
                window.google.translate.TranslateElement &&
                window.google.translate.TranslateElement.InlineLayout
            ) {
                // Clear previous instance
                googleTranslateRef.current.innerHTML = '';

                new window.google.translate.TranslateElement(
                    {
                        pageLanguage: 'en',
                        includedLanguages: 'en,es,fr',
                        layout: window.google.translate.TranslateElement.InlineLayout.VERTICAL,
                        autoDisplay: false,
                        defaultLanguage: languageCode,
                    },
                    googleTranslateRef.current,
                );

                // Simple initialization - let Google Translate handle the rest
                setTimeout(() => {
                    try {
                        const languageSelector = document.querySelector('.goog-te-combo') as HTMLSelectElement;
                        if (languageSelector) {
                            languageSelector.value = languageCode;
                        }
                    } catch (error) {
                        console.warn('Error setting initial language:', error);
                    }
                }, 300);
            }
        } catch (error) {
            console.warn('Error initializing Google Translate:', error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        try {
            // Check for existing language setting on component mount
            const currentLang = getCurrentLanguage();
            const languageOption = getLanguageOption(currentLang);
            setSelectedLanguage(languageOption);

            // Set up the global initialization function
            window.googleTranslateElementInit = () => {
                try {
                    setIsInitialized(true);
                    initializeGoogleTranslate(selectedLanguage.code);
                } catch (error) {
                    console.warn('Error in googleTranslateElementInit:', error);
                    setIsLoading(false);
                }
            };

            // Load Google Translate script if not already loaded
            if (!window.google || !window.google.translate) {
                loadGoogleTranslateScript();
            } else {
                setIsInitialized(true);
                initializeGoogleTranslate(selectedLanguage.code);
            }
        } catch (error) {
            console.warn('Error in useEffect:', error);
            setIsLoading(false);
        }

        return () => {
            window.googleTranslateElementInit = undefined;
        };
    }, []); // Empty dependency array - only run once on mount

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <>
            <div ref={googleTranslateRef} className={Styles.googleTranslateRef}></div>
            <div ref={dropdownRef} className={Styles.dropdown}>
                <button onClick={toggleDropdown} className={Styles.dropdownButton} disabled={isLoading}>
                    {isLoading ? (
                        <div className={Styles.loader}></div>
                    ) : (
                        <img src={selectedLanguage.flag} alt={selectedLanguage.name} className={Styles.flagIcon} />
                    )}
                </button>
                {isOpen && !isLoading && (
                    <div className={Styles.dropdownMenu}>
                        {options.map((option) => (
                            <div
                                key={option.code}
                                onClick={() => handleOptionSelect(option)}
                                className={`${Styles.dropdownItem} ${selectedLanguage.code === option.code ? Styles.active : ''}`}>
                                <img src={option.flag} alt={option.name} className={Styles.flagIcon} />
                                <span>{option.name}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

export default TranslateComponent;
